import { useState } from "react";
import { Calculator } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";

const SyringeVisual = ({ units }) => {
  const capped = Math.max(0, Math.min(units, 100));
  const over = units > 100;
  const x0 = 46;
  const barrelW = 300;
  const fillW = (capped / 100) * barrelW;
  const ticks = [];
  for (let u = 0; u <= 100; u += 5) {
    const x = x0 + (u / 100) * barrelW;
    const major = u % 10 === 0;
    ticks.push(
      <g key={u}>
        <line x1={x} y1={30} x2={x} y2={major ? 44 : 38} stroke="#8C7B6B" strokeWidth={major ? 1.4 : 0.8} />
        {major && u > 0 && (
          <text x={x} y={56} textAnchor="middle" fontSize="9" fill="#6E675F" fontWeight="600">{u}</text>
        )}
      </g>
    );
  }
  return (
    <div className="mt-6" data-testid="syringe-visual">
      <svg viewBox="0 0 420 96" className="w-full" role="img" aria-label={`Syringe drawn to ${capped.toFixed(0)} units`}>
        {/* needle */}
        <line x1={4} y1={47} x2={x0 - 14} y2={47} stroke="#6E675F" strokeWidth={2.5} strokeLinecap="round" />
        {/* hub */}
        <rect x={x0 - 14} y={38} width={14} height={18} rx={3} fill="#DDD7CF" stroke="#8C7B6B" strokeWidth={1} />
        {/* barrel */}
        <rect x={x0} y={28} width={barrelW} height={38} rx={6} fill="#FFFFFF" stroke="#8C7B6B" strokeWidth={1.5} />
        {/* liquid fill */}
        {fillW > 0 && (
          <rect x={x0} y={29.5} width={fillW} height={35} rx={5} fill={over ? "#C0392B" : "#8C7B6B"} opacity={0.35} style={{ transition: "width 300ms ease" }} />
        )}
        {/* ticks */}
        {ticks}
        {/* plunger head at draw mark */}
        <rect x={x0 + fillW - 2} y={26} width={4} height={42} rx={2} fill={over ? "#C0392B" : "#5F544A"} />
        {/* plunger rod + thumb rest */}
        <line x1={x0 + fillW + 2} y1={47} x2={x0 + barrelW + 44} y2={47} stroke="#B4AAA0" strokeWidth={4} strokeLinecap="round" />
        <rect x={x0 + barrelW + 44} y={33} width={7} height={28} rx={3} fill="#B4AAA0" />
        {/* flanges */}
        <rect x={x0 + barrelW - 2} y={20} width={5} height={54} rx={2} fill="#DDD7CF" stroke="#8C7B6B" strokeWidth={0.8} />
        {/* IU label */}
        <text x={x0 + 6} y={90} fontSize="9" fill="#6E675F" fontWeight="600" letterSpacing="1">U-100 · 1 mL INSULIN SYRINGE</text>
      </svg>
      {units > 0 && (
        <p className={`text-sm mt-2 text-center font-semibold ${over ? "text-red-700" : "text-[#1A1A1A]"}`} data-testid="syringe-draw-label">
          {over
            ? `Dose exceeds one 1 mL syringe (${units.toFixed(0)} units) — split across multiple draws`
            : `Draw to the ${capped.toFixed(0)} unit mark`}
        </p>
      )}
    </div>
  );
};

export const ReconstitutionCalculator = () => {
  const [peptideMg, setPeptideMg] = useState(0);
  const [waterMl, setWaterMl] = useState(0);
  const [doseValue, setDoseValue] = useState(0);
  const [doseUnit, setDoseUnit] = useState("mg");

  const toggleDoseUnit = (toMcg) => {
    const next = toMcg ? "mcg" : "mg";
    if (next === doseUnit) return;
    const v = parseFloat(doseValue);
    if (v) setDoseValue(next === "mcg" ? +(v * 1000).toFixed(3) : +(v / 1000).toFixed(6));
    setDoseUnit(next);
  };

  const totalMcg = (parseFloat(peptideMg) || 0) * 1000;
  const water = parseFloat(waterMl) || 0;
  const concMcgPerMl = water > 0 ? totalMcg / water : 0; // mcg per mL
  const concMgPerMl = concMcgPerMl / 1000;
  const dose = (parseFloat(doseValue) || 0) * (doseUnit === "mg" ? 1000 : 1); // always mcg
  const drawMl = concMcgPerMl > 0 ? dose / concMcgPerMl : 0;
  const drawUnits = drawMl * 100; // insulin syringe units (U-100: 1 mL = 100 units)
  const dosesPerVial = dose > 0 ? Math.floor(totalMcg / dose) : 0;

  return (
    <div className="border border-[#DDD7CF] rounded-lg bg-white p-6" data-testid="reconstitution-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Calculator className="w-5 h-5 text-[#8C7B6B]" />
        <h3 className="font-display text-base uppercase tracking-wide text-[#1A1A1A]">Reconstitution Calculator</h3>
      </div>
      <p className="text-xs text-[#6E675F] mb-5">Estimate concentration and draw volume for your research protocol.</p>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="calc-peptide" className="text-xs">Peptide (mg)</Label>
          <Input id="calc-peptide" data-testid="calc-peptide-mg" type="number" min="0" step="0.5" value={peptideMg} onChange={(e) => setPeptideMg(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="calc-water" className="text-xs">Bac. Water (mL)</Label>
          <Input id="calc-water" data-testid="calc-water-ml" type="number" min="0" step="0.5" value={waterMl} onChange={(e) => setWaterMl(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="calc-dose" className="text-xs">Dose (<span data-testid="calc-dose-unit-label">{doseUnit}</span>)</Label>
          <Input id="calc-dose" data-testid="calc-dose-input" type="number" min="0" step={doseUnit === "mg" ? "0.05" : "50"} value={doseValue} onChange={(e) => setDoseValue(e.target.value)} className="mt-1" />
          <div className="mt-2 flex items-center justify-end gap-1.5 select-none" data-testid="calc-dose-unit-toggle">
            <button type="button" onClick={() => toggleDoseUnit(false)} className={`text-[11px] font-semibold ${doseUnit === "mg" ? "text-[#1A1A1A]" : "text-[#9C948B]"}`} data-testid="calc-dose-unit-mg">mg</button>
            <Switch checked={doseUnit === "mcg"} onCheckedChange={toggleDoseUnit} className="scale-75 data-[state=checked]:bg-[#8C7B6B] data-[state=unchecked]:bg-[#8C7B6B]" aria-label="Switch dose unit between mg and mcg" data-testid="calc-dose-unit-switch" />
            <button type="button" onClick={() => toggleDoseUnit(true)} className={`text-[11px] font-semibold ${doseUnit === "mcg" ? "text-[#1A1A1A]" : "text-[#9C948B]"}`} data-testid="calc-dose-unit-mcg">mcg</button>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-[#8C7B6B] font-semibold mt-2" data-testid="calc-mg-mcg-hint">
        Conversion: 1 mg = 1000 mcg{dose > 0 ? ` · your dose = ${doseUnit === "mg" ? `${dose.toFixed(0)} mcg` : `${(dose / 1000).toFixed(3).replace(/\.?0+$/, "")} mg`}` : ""}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
        <Stat label="Concentration" value={`${concMgPerMl.toFixed(2)} mg/mL`} testid="calc-concentration" />
        <Stat label="Draw Volume" value={`${drawMl.toFixed(3)} mL`} testid="calc-draw-ml" />
        <Stat label="Syringe Units" value={`${drawUnits.toFixed(0)} IU`} testid="calc-draw-units" highlight />
        <Stat label="Doses / Vial" value={`${dosesPerVial}`} testid="calc-doses" />
      </div>

      <SyringeVisual units={drawUnits} />

      <p className="text-[10px] text-[#6E675F] mt-4">
        Units are for a U-100 insulin syringe (1 mL = 100 units). For research calculation only — not dosing guidance for human use.
      </p>
    </div>
  );
};

const Stat = ({ label, value, testid, highlight }) => (
  <div className={`rounded-lg p-3 text-center ${highlight ? "bg-[#8C7B6B] text-white" : "bg-[#EFEBE6]"}`}>
    <p className={`text-[10px] tracking-[0.15em] uppercase font-semibold ${highlight ? "text-white/80" : "text-[#6E675F]"}`}>{label}</p>
    <p className={`text-base font-semibold mt-1 ${highlight ? "text-white" : "text-[#1A1A1A]"}`} data-testid={testid}>{value}</p>
  </div>
);
